
"use client"

import { useState } from "react"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import type { SleepPeriod, SleepEvent } from "@/lib/types";
import { format, isSameDay, startOfDay, endOfDay, areIntervalsOverlapping, addDays, subDays } from "date-fns";
import { Timer, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Milk } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";


type AwakeWindowsChartProps = {
  periods: SleepPeriod[];
  events: SleepEvent[];
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

const getTimelineSegments = (periods: SleepPeriod[], events: SleepEvent[], date: Date) => {
    const segments: any[] = [];
    if (periods.length === 0 && events.length === 0) return [];

    const dayStart = startOfDay(date);
    const dayEnd = endOfDay(date);

    let currentTime = dayStart;
    
    const relevantPeriods = periods.filter(p => areIntervalsOverlapping(
        { start: p.startTime, end: p.endTime },
        { start: dayStart, end: dayEnd }
    ));
    
    relevantPeriods.sort((a,b) => a.startTime.getTime() - b.startTime.getTime());

    relevantPeriods.forEach(period => {
        const periodStartInView = period.startTime < dayStart ? dayStart : period.startTime;
        const periodEndInView = period.endTime > dayEnd ? dayEnd : period.endTime;

        // Awake window before period
        if (periodStartInView > currentTime) {
            segments.push({
                start: currentTime,
                end: periodStartInView,
                type: 'awake'
            });
        }
        
        // Handle sleep with potential wake-ups
        if (period.type === 'night' && period.wakeUps && period.wakeUps.length > 0) {
            let sleepSegmentStart = periodStartInView;
            const sortedWakeUps = [...period.wakeUps]
                .filter(w => w.time >= dayStart && w.time < dayEnd)
                .sort((a,b) => a.time.getTime() - b.time.getTime());

            sortedWakeUps.forEach(wakeUp => {
                const wakeUpStart = wakeUp.time;
                const wakeUpEnd = new Date(wakeUpStart.getTime() + wakeUp.duration * 60 * 1000);
                
                if (wakeUpStart > sleepSegmentStart) {
                    segments.push({ start: sleepSegmentStart, end: wakeUpStart, type: 'night' });
                }
                segments.push({ start: wakeUpStart, end: wakeUpEnd, type: 'wakeup' });
                sleepSegmentStart = wakeUpEnd;
            });
            
            if (periodEndInView > sleepSegmentStart) {
                 segments.push({ start: sleepSegmentStart, end: periodEndInView, type: 'night' });
            }
        } else {
            segments.push({
                start: periodStartInView,
                end: periodEndInView,
                type: period.type
            });
        }

        currentTime = periodEndInView;
    });

    if (currentTime < dayEnd) {
        segments.push({
            start: currentTime,
            end: dayEnd,
            type: 'awake'
        });
    }

    const totalMinutes = 24 * 60;
    
    const feedEvents = events.filter(e => e.type === 'feed' && isSameDay(e.time.toDate(), date));

    const finalSegments = segments
        .filter(s => s.end > s.start)
        .map(segment => {
            const startOfDayTime = startOfDay(segment.start).getTime();
            const startMinutes = (segment.start.getTime() - startOfDayTime) / (1000 * 60);
            const endMinutes = Math.min((segment.end.getTime() - startOfDayTime) / (1000 * 60), totalMinutes);
            const durationMinutes = endMinutes - startMinutes;

            const durationHours = Math.floor(durationMinutes / 60);
            const remainingMinutes = Math.round(durationMinutes % 60);
            const durationText = `${durationHours > 0 ? `${durationHours}h` : ''} ${remainingMinutes > 0 ? `${remainingMinutes}m` : ''}`.trim();

            return {
                type: segment.type,
                startPercentage: (startMinutes / totalMinutes) * 100,
                widthPercentage: (durationMinutes / totalMinutes) * 100,
                startTime: format(segment.start, "HH:mm"),
                endTime: format(segment.end, "HH:mm"),
                durationText,
                durationMinutes,
            };
    });

    feedEvents.forEach(feed => {
        const feedTime = feed.time.toDate();
        const startMinutes = (feedTime.getTime() - dayStart.getTime()) / (1000 * 60);
        finalSegments.push({
            type: 'feed',
            startPercentage: (startMinutes / totalMinutes) * 100,
            widthPercentage: 0,
            startTime: format(feedTime, "HH:mm"),
            endTime: format(feedTime, "HH:mm"),
            durationText: 'Feed',
            durationMinutes: 0,
        })
    });

    return finalSegments;
};

export function AwakeWindowsChart({ periods, events, selectedDate, onDateChange }: AwakeWindowsChartProps) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const segments = getTimelineSegments(periods, events, selectedDate);
  const loggedDates = [...new Set(events.map(e => startOfDay(e.time.toDate()).getTime()))];

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
        onDateChange(date);
        setDatePickerOpen(false);
    }
  }
  
  const hasPrevDay = loggedDates.some(d => d < startOfDay(selectedDate).getTime());
  // hasNextDay variable removed - unused


  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
             <div className="flex items-center gap-2">
                <Timer className="h-5 w-5 text-muted-foreground" />
                <CardTitle className="text-lg font-medium">Daily Timeline</CardTitle>
            </div>
            <div className="flex items-center gap-2 self-end sm:self-auto">
                <Button variant="outline" size="icon" onClick={() => onDateChange(subDays(selectedDate,1))} disabled={!hasPrevDay}>
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                 <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "justify-center text-center font-normal",
                              !selectedDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {isSameDay(selectedDate, new Date()) ? "Today" : format(selectedDate, "PP")}
                          </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={handleDateChange}
                          disabled={(date) => date > new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                </Popover>
                <Button variant="outline" size="icon" onClick={() => onDateChange(addDays(selectedDate, 1))} disabled={isSameDay(selectedDate, new Date())}>
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {segments.length > 0 ? (
          <TooltipProvider>
            <div className="space-y-4">
                 <div className="w-full">
                    <div className="relative h-10 w-full rounded-lg bg-muted overflow-hidden border-2 border-border shadow-sm">
                        {segments.filter(s => s.widthPercentage).map((segment, index) => (
                          <Tooltip key={index}>
                            <TooltipTrigger asChild>
                                <div
                                    className={cn(
                                        `absolute h-full flex items-center justify-center text-xs font-semibold border-r border-background/30 transition-opacity hover:opacity-90`,
                                        segment.type === 'night' && 'bg-primary text-primary-foreground shadow-inner',
                                        segment.type === 'nap' && 'bg-accent text-accent-foreground shadow-inner border-accent-foreground/10',
                                        segment.type === 'wakeup' && 'shadow-inner',
                                        segment.type === 'awake' && 'bg-transparent'
                                    )}
                                    style={{
                                        left: `${segment.startPercentage}%`,
                                        width: `${segment.widthPercentage}%`,
                                        ...(segment.type === 'wakeup' && {
                                            backgroundColor: 'hsl(30, 75%, 55%)',
                                            color: 'white'
                                        })
                                    }}
                                >
                                </div>
                            </TooltipTrigger>
                             <TooltipContent>
                                <p className="capitalize font-semibold">{segment.type} Window</p>
                                <p className="text-sm">{segment.startTime} - {segment.endTime} ({segment.durationText})</p>
                              </TooltipContent>
                          </Tooltip>
                        ))}
                         {segments.filter(s => s.type === 'feed').map((segment, index) => (
                           <Tooltip key={`feed-${index}`}>
                            <TooltipTrigger asChild>
                               <div
                                  className="absolute h-full flex items-center justify-center"
                                  style={{ left: `calc(${segment.startPercentage}% - 6px)` }}
                                >
                                 <Milk className="h-4 w-4 text-foreground/70" />
                               </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p className="capitalize">Feed</p>
                                <p className="text-sm text-muted-foreground">{segment.startTime}</p>
                            </TooltipContent>
                           </Tooltip>
                        ))}
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground mt-1 px-1">
                        <span>00:00</span>
                        <span>06:00</span>
                        <span>12:00</span>
                        <span>18:00</span>
                        <span>24:00</span>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-primary border border-primary-foreground/20 shadow-sm"></div>
                        <span className="font-medium">Night Sleep</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-accent border-2 border-border shadow-sm"></div>
                        <span className="font-medium">Day Naps</span>
                    </div>
                     <div className="flex items-center gap-2">
                        <Milk className="h-4 w-4 text-foreground/80" />
                        <span className="font-medium">Feed</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded shadow-sm border border-white/20" style={{ backgroundColor: 'hsl(30, 75%, 55%)' }}></div>
                        <span className="font-medium">Wake-up</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-muted border-2 border-border shadow-sm"></div>
                        <span className="font-medium">Awake</span>
                    </div>
                </div>
            </div>
          </TooltipProvider>
        ) : (
            <div className="text-center text-sm text-muted-foreground py-8">
                No sleep data logged for {format(selectedDate, 'PPP')}.
            </div>
        )}
      </CardContent>
    </Card>
  )
}
